import { DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as pdfMake from 'pdfmake/build/pdfmake';
import { TDocumentDefinitions, TFontDictionary } from 'pdfmake/interfaces';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { GridFSFile } from '../../../models/file.model';
import {
  DeceasedReport,
  DeceasedReportStatus,
  ReportResultStatus,
  ReportType,
} from '../../../models/report.model';
import { ResponseSingle } from '../../../models/response.model';
import { getDateDaysAgo } from '../../shared/utils/date.utils';

const reports = require('./reports.json');

@Injectable({
  providedIn: 'root',
})
export class ReportService {
  public reportResponse: BehaviorSubject<
    ResponseSingle<DeceasedReport> | undefined
  > = new BehaviorSubject<ResponseSingle<DeceasedReport> | undefined>(
    undefined,
  );

  private _apiUrl = environment.apiUrl;
  private _baseUrl = environment.baseUrl;

  constructor(
    private _http: HttpClient,
    private _datePipe: DatePipe,
  ) {}

  public getFreeQuestions(): Observable<any> {
    return of(reports.free.questions);
  }

  public gerFreeDefinitions(): Observable<any> {
    return of(reports.free.definitions);
  }

  public getReport(id: string): Observable<ResponseSingle<DeceasedReport>> {
    return this._http.get<ResponseSingle<DeceasedReport>>(
      `${this._apiUrl}/reports/${id}`,
    );
  }

  public getReportStatus(
    id: string,
  ): Observable<ResponseSingle<DeceasedReportStatus>> {
    return this._http.get<ResponseSingle<DeceasedReportStatus>>(
      `${this._apiUrl}/reports/${id}/status`,
    );
  }

  public mapPrimaryReportResult(result: ReportResultStatus) {
    switch (result) {
      case ReportResultStatus.EXISTS:
        return 'TAK';
      case ReportResultStatus.PENDING:
        return 'GENERUJĘ';
      case ReportResultStatus.SKIPPED:
        return 'POMINIĘTY';
      case ReportResultStatus.NOT_EXISTS:
        return 'NIE';
    }
  }

  public getExtendedReportPDF(report: DeceasedReport, id: string) {

    if (report.type != ReportType.EXTENDED) {
      return;
    }

    const fileId = this._getExtendedReportFileId(report);

    if (!fileId) {
      return;
    }

    const url = `${this._apiUrl}/reports/${encodeURIComponent(id)}/file/${encodeURIComponent(fileId)}`;
    this._savePDF(url, report.reportNumber);
  }

  private _savePDF(url:string, reportNumber:string): void {
    const sanitizedReportNumber = reportNumber.replaceAll('/', '_');

    fetch(url)
      .then(response => response.blob())
      .then(blob => {
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `Raport_${sanitizedReportNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);

      });
  }

  private _getExtendedReportFileId(
    report: Extract<DeceasedReport, { type: ReportType.EXTENDED }>,
  ): string | null {
    if (report.reportFile?._id) {
      return report.reportFile._id;
    }

    const reportRecord = report as unknown as Record<string, unknown>;
    const resultRecord = reportRecord['result'] as Record<string, unknown> | undefined;
    const reportFileRecord = reportRecord['reportFile'] as Record<string, unknown> | undefined;

    const directCandidates: unknown[] = [
      reportFileRecord?.['_id'],
      reportRecord['fileId'],
      reportRecord['pdfFileId'],
      resultRecord?.['fileId'],
      resultRecord?.['pdfFileId'],
    ];

    for (const candidate of directCandidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate;
      }
    }

    const files = this._getExtendedReportFiles(report);
    const selectedFile = this._getPreferredPdfFile(files);
    return selectedFile?._id ?? null;
  }

  private _getExtendedReportFiles(
    report: Extract<DeceasedReport, { type: ReportType.EXTENDED }>,
  ): GridFSFile[] {
    const fileGroups = [
      report.result.ceidg.files,
      report.result.krs.propertyRights.files,
      report.result.krs.companyAuthorities.files,
      report.result.crbr.files,
      report.result.krz.files,
      report.result.msig.files,
      report.result.vatWl.files,
      report.result.sudop.files,
    ];

    return fileGroups.reduce<GridFSFile[]>((acc, group) => {
      if (Array.isArray(group) && group.length > 0) {
        acc.push(...group);
      }
      return acc;
    }, []);
  }

  private _getPreferredPdfFile(files: GridFSFile[]): GridFSFile | null {
    const sourcePdfFile = files.find(
      (file) =>
        file.source?.toLowerCase().includes('report') &&
        (file.contentType?.toLowerCase() === 'application/pdf' ||
          file.name?.toLowerCase().endsWith('.pdf')),
    );

    if (sourcePdfFile) {
      return sourcePdfFile;
    }

    const pdfFile = files.find(
      (file) =>
        file.contentType?.toLowerCase() === 'application/pdf' ||
        file.name?.toLowerCase().endsWith('.pdf'),
    );

    return pdfFile ?? files[0] ?? null;
  }

  private _getFileNameFromDisposition(
    contentDisposition: string | null,
  ): string | null {
    if (!contentDisposition) {
      return null;
    }

    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
    if (utf8Match?.[1]) {
      return decodeURIComponent(utf8Match[1]);
    }

    const quotedMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
    return quotedMatch?.[1] ?? null;
  }

  public getPrimaryReportPDF(report: DeceasedReport, id: string) {
    if (report.type != ReportType.PRIMARY) {
      return;
    }
    const fonts: TFontDictionary = {
      Roboto: {
        normal:
          'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
        bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
        italics:
          'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Italic.ttf',
        bolditalics:
          'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-MediumItalic.ttf',
      },
      HelveticaNeue: {
        normal: `${this._baseUrl}/assets/fonts/HelveticaNeueLight.ttf`,
        bold: `${this._baseUrl}/assets/fonts/HelveticaNeueMedium.ttf`,
        italics: `${this._baseUrl}/assets/fonts/HelveticaNeueLightItalic.ttf`,
        // bolditalics: `${this._baseUrl}/assets/fonts/HelveticaNeueLightItalic.ttf`
      },
    };

    const docDef: TDocumentDefinitions = {
      content: [
        {
          text: 'SYTUACJA OSOBY ZMARŁEJ \nW PUBLICZNYCH BAZACH DANYCH',
          style: 'header',
          alignment: 'center',
          margin: [0, 50, 0, 50],
        },
        {
          table: {
            widths: ['*', 'auto', 'auto'],
            body: [
              [
                {
                  text: [
                    {
                      text: [
                        {
                          text: `${report.deceased.name} ${report.deceased.lastName}\n`,
                          bold: true,
                          fontSize: 12,
                          lineHeight: 1.5,
                        },
                        {
                          text: [
                            { text: 'PESEL: ', bold: true, fontSize: 10, lineHeight: 1.2 },
                            { text: report.deceased.peselNumber + '\n', fontSize: 10, lineHeight: 1.2 },
                          ],
                        },
                      ],
                    },
                    {
                      text: [
                        { text: 'NIP: ', bold: true, fontSize: 10, lineHeight: 1.1 },
                        { text: report.deceased.taxId + '\n', fontSize: 10, lineHeight: 1.2 },
                      ],
                    },
                    {
                      text: [
                        { text: 'Data zgonu: ', bold: true, fontSize: 10, lineHeight: 1.2 },
                        {
                          text:
                            this._datePipe.transform(report.deceased.dateOfDeath, 'longDate') + '\n',
                          fontSize: 10,
                          lineHeight: 1.2,
                        },
                      ],
                    },
                  ],
                },
                {
                  text: [
                    {
                      text: 'Zamawiający\n',
                      bold: true,
                      fontSize: 12,
                      lineHeight: 1.5
                    },
                    {
                      text: `${report.order.client.name} ${report.order.client.lastName}\n`,
                      fontSize: 10,
                      lineHeight: 1.2,
                    },
                  ],
                },
                {
                  qr: `${this._baseUrl}/raport/${id}`,
                  foreground: '#676767',
                  fit: 110,
                  alignment: 'right',
                  marginTop: 8,
                  marginRight: 4,
                },
              ],
              [
                {
                  colSpan: 2,
                  text: `Wygenerowano na podstawie informacji z publicznych baz danych. Data wygenerowania informacji z baz danych: ${this._datePipe.transform(report.databaseStatusOn, 'd MMMM y, HH:mm:ss')} `,
                  italics: true,
                  fontSize: 8,
                  marginTop: -5,
                },
                {},
                {},
              ],
            ],
          },
          layout: {
            fillColor: '#f7f7f7',
            paddingLeft: () => 5,
            paddingRight: () => 5,
            paddingBottom: () => 5,
            paddingTop: () => 5,
            defaultBorder: false,
          },
        },

        {
          text: ' ',
          margin: [0, 30, 0, 0],
        },
        {
          table: {
            headerRows: 1,
            widths: ['*', 'auto'],
            body: [
              [
                {
                  bold: true,
                  text: 'BAZA DANYCH',
                },
                {
                  text: 'WYNIK',
                  bold: true,
                },
              ],

              //CEIDG
              [
                {
                  text: [
                    {
                      text: 'Centralna Ewidencja Działalności Gospodarczej\n',
                      bold: true,
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text: 'Czy osoba zmarła prowadziła działalność gospodarczą?\n',
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text:
                        'Prowadzenie działalności gospodarczej wiąże się z możliwością występowania aktywów,' +
                        ' ale również z możliwością występowania zadłużenia w stosunku do administracji' +
                        ' publicznej np. ZUS, US lub kontrahentów.',
                      fontSize: 8,
                      italics: true,
                    },
                  ],
                },
                {
                  text: [
                    this.mapPrimaryReportResult(report.result.ceidg),
                    ...(report.result.ceidg === 'SKIPPED'
                      ? [
                        {
                          text: '\n(nie podano NIP)',
                          fontSize: 7,
                          alignment: 'center',
                        },
                      ]
                      : []),
                  ],
                  bold: true,
                  fontSize: 10,
                  alignment: 'center',
                },
              ],

              //KRS #1
              [
                {
                  text: [
                    {
                      text: 'Krajowy Rejestr Sądowy\n',
                      bold: true,
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text:
                        'Czy osoba zmarła posiadała prawa własności np. udziały lub akcje w' +
                        ' spółkach?*\n',
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text:
                        'Posiadanie udziałów w spółkach jest aktywem, jednak jest to uzależnione od' +
                        ' kondycji danej spółki.',
                      fontSize: 8,
                      italics: true,
                    },
                  ],
                },
                {
                  text: [
                    this.mapPrimaryReportResult(report.result.krs.propertyRights),
                    ...(report.result.krs.propertyRights === 'SKIPPED'
                      ? [
                        {
                          text: '\n(nie podano NIP)',
                          fontSize: 7,
                          alignment: 'center',
                        },
                      ]
                      : []),
                  ],
                  bold: true,
                  fontSize: 10,
                  alignment: 'center',
                },
              ],

              //KRS #2
              [
                {
                  text: [
                    {
                      text: 'Krajowy Rejestr Sądowy\n',
                      bold: true,
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text:
                        'Czy osoba zmarła pełniła funkcje we władzach spółki np. jako członek' +
                        ' zarządu spółki lub jej wspólnik?*\n',
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text:
                        'Pełnienie niektórych funkcji w spółce może się wiązać z odpowiedzialnością' +
                        ' osobistą za zobowiązania spółki. Może to być potencjalne źródło długów' +
                        ' osoby zmarłej.',
                      fontSize: 8,
                      italics: true,
                    },
                  ],
                },
                {
                  text: [
                    this.mapPrimaryReportResult(report.result.krs.companyAuthorities),
                    ...(report.result.krs.companyAuthorities === 'SKIPPED'
                      ? [
                        {
                          text: '\n(nie podano NIP)',
                          fontSize: 7,
                          alignment: 'center',
                        },
                      ]
                      : []),
                  ],
                  bold: true,
                  fontSize: 10,
                  alignment: 'center',
                },
              ],

              //CRBR
              [
                {
                  text: [
                    {
                      text: 'Centralny Rejestr Beneficjentów Rzeczywistych\n',
                      bold: true,
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text:
                        'Czy osoba zmarła jest beneficjentem spółki, trustu lub fundacji' +
                        ' rodzinnej?\n',
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text:
                        'Występowanie osoby zmarłej w tym rejestrze może oznaczać posiadanie przez' +
                        ' nią aktywów.',
                      fontSize: 8,
                      italics: true,
                    },
                  ],
                },
                {
                  text: [
                    this.mapPrimaryReportResult(report.result.crbr),
                    ...(report.result.crbr === 'SKIPPED'
                      ? [
                        {
                          text: '\n(nie podano NIP)',
                          fontSize: 7,
                          alignment: 'center',
                        },
                      ]
                      : []),
                  ],
                  bold: true,
                  fontSize: 10,
                  alignment: 'center',
                },
              ],

              //VAT
              [
                {
                  text: [
                    {
                      text: 'Wykaz Podatników Podatku VAT\n',
                      bold: true,
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text:
                        'Czy osoba zmarła występuje w Wykazie podatników podatku VAT, czyli białej' +
                        ' liście podatników VAT?\n',
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text: 'To jest informacja czy osoba zmarła kiedykolwiek posidała status podatku VAT (czy występowała w rejestrze VAT). Rodzaj tego statusu może oznaczać posiadanie aktywów albo zobowiązań np. wobec Urzędu Skarbowego.',
                      fontSize: 8,
                      italics: true,
                    },
                  ],
                },
                {
                  text: [
                    this.mapPrimaryReportResult(report.result.vatWl),
                    ...(report.result.vatWl === 'SKIPPED'
                      ? [
                        {
                          text: '\n(nie podano NIP)',
                          fontSize: 7,
                          alignment: 'center',
                        },
                      ]
                      : []),
                  ],
                  bold: true,
                  fontSize: 10,
                  alignment: 'center',
                },
              ],

              //KRZ
              [
                {
                  text: [
                    {
                      text: 'Krajowy Rejestr Zadłużonych\n',
                      bold: true,
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text: 'Czy osoba zmarła występuje w Krajowym Rejestrze Zadłużonych?\n',
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text:
                        'Występowanie osoby zmarłej w tym rejestrze może oznaczać posiadanie przez' +
                        ' nią długów. To jest bardzo istotna informacja.',
                      fontSize: 8,
                      italics: true,
                    },
                  ],
                },
                {
                  text: [
                    this.mapPrimaryReportResult(report.result.krz),
                    ...(report.result.krz === 'SKIPPED'
                      ? [
                        {
                          text: '\n(nie podano NIP)',
                          fontSize: 7,
                          alignment: 'center',
                        },
                      ]
                      : []),
                  ],
                  bold: true,
                  fontSize: 10,
                  alignment: 'center',
                },
              ],

              //SUDOP
              [
                {
                  pageBreak: 'before',
                  text: [
                    {
                      text: 'SUDOP\n',
                      bold: true,
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text: 'Czy osoba zmarła otrzymała pomoc publiczną?\n',
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text:
                        'Pewne formy pomocy publicznej np. odroczenie zapłaty mandatu lub kary mogą' +
                        ' być źrodłem długów osoby zmarłej, jeżeli nie zostały zapłacone.',
                      fontSize: 8,
                      italics: true,
                    },
                  ],
                },
                {
                  pageBreak: 'before',
                  text: [
                    this.mapPrimaryReportResult(report.result.sudop),
                    ...(report.result.sudop === 'SKIPPED'
                      ? [
                        {
                          text: '\n(nie podano NIP)',
                          fontSize: 7,
                          alignment: 'center',
                        },
                      ]
                      : []),
                  ],
                  bold: true,
                  fontSize: 10,
                  alignment: 'center',
                },
              ],

              //MSIG
              [
                {
                  text: [
                    {
                      text: 'Monitor Sądowy i Gospodarczy\n',
                      bold: true,
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text: 'Czy osoba zmarła występuje w Monitorze Sądowym i Gospodarczym?\n',
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text:
                        'Występowanie osoby zmarłej w tym rejestrze może oznaczać posiadanie przez' +
                        ' nią długów. To jest bardzo istotna informacja.',
                      fontSize: 8,
                      italics: true,
                    },
                  ],
                },
                {
                  text: [
                    this.mapPrimaryReportResult(report.result.msig),
                    ...(report.result.msig === 'SKIPPED'
                      ? [
                        {
                          text: '\n(nie podano NIP)',
                          fontSize: 7,
                          alignment: 'center',
                        },
                      ]
                      : []),
                  ],
                  bold: true,
                  fontSize: 10,
                  alignment: 'center',
                },
              ],
            ],
          },
          layout: {
            fillColor: function (rowIndex: any) {
              return rowIndex % 2 === 0 ? '#f7f7f7' : null;
            },
            hLineColor: () => '#c9c9c9',
            vLineColor: () => '#c9c9c9',
            defaultBorder: true,
            //@ts-ignore
            // border: [true, true, true, true],
            paddingLeft: () => 5,
            paddingRight: () => 5,
            paddingTop: () => 5,
            paddingBottom: () => 5,
          },
        },
        {
          pageBreak: 'before',
          table: {
            headerRows: 1,
            widths: ['100%'],
            body: [
              [
                {
                  bold: true,
                  text: 'DEFINICJE',
                },
              ],

              [
                {
                  text: [
                    {
                      text: 'Beneficjent rzeczywisty\n',
                      bold: true,
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text:
                        'Osoba, która sprawowała kontrolę nad danym podmiotem np. spółką' +
                        ' lub miała decydujący wpływ na jego czynności lub działania, była udziałowcem lub akcjonariuszem mającym prawo własności więcej niż 25% ogólnej liczby udziałów lub akcji, dysponowała więcej niż 25% ogólnej liczby głosów w organie stanowiącym spółki lub sprawowała kontrolę nad osobą prawną lub osobami prawnymi w sposób opisany w art. 2 ust. 2 pkt 1 ustawy AML (Dz.U. 2022 poz. 593), sprawowała kontrolę nad osobą prawną przez posiadanie w stosunku do niej uprawnień wymienionych w art. 3 ust. 1 pkt 37 ustawy z dnia 29 września 1994 r. o rachunkowości (Dz. U. z 2019 r. poz. 351), była fundatorem w rozumieniu ustawy z dnia 26 stycznia 2023 r. o fundacji rodzinnej (Dz. U. poz. 326) albo beneficjentem w rozumieniu tej ustawy, była założycielem trustu, jego beneficjentem lub sprawowała kontrolę nad trustem.',
                      fontSize: 10,
                    },
                  ],
                },
              ],

              [
                {
                  text: [
                    {
                      text: 'Aktywa\n',
                      bold: true,
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text:
                        'Zasoby majątkowe o określonej wartości lub inaczej majątek np.' +
                        ' maszyny, pojazdy, nieruchomości, wierzytelności, środki na rachunkach bankowych, udziały w spółkach, itp.',
                      fontSize: 10,
                    },
                  ],
                },
              ],

              [
                {
                  text: [
                    {
                      text: 'Pasywa\n',
                      bold: true,
                      fontSize: 10,
                      lineHeight: 1.3,
                    },
                    {
                      text:
                        'Zasoby zmniejszające majątek lub takie, z których finansowany jest' +
                        ' majątek np. kredyty, pożyczki oraz długi i zobowiązaniach wobec podmiotów, którym osoba zmarła w jakiś sposób była winna pieniądze.',
                      fontSize: 10,
                    },
                  ],
                },
              ],
            ],
          },
          layout: {
            fillColor: (rowIndex: any) => {
              return rowIndex % 2 === 0 ? '#f7f7f7' : null;
            },
            hLineColor: () => '#c9c9c9',
            vLineColor: () => '#c9c9c9',
            paddingLeft: () => 5,
            paddingRight: () => 5,
            paddingTop: () => 5,
            paddingBottom: () => 5,
            //@ts-ignore
            border: [false, true, false, false],
          },
        },
        {
          text: '',
          marginTop: 20,
        },
        {
          text:
            'Pamiętaj, że jeżeli osoba zmarła posiadała samodzielnie lub łącznie z innymi osobami mniej niż' +
            ' 10% udziałów spółki z ograniczoną odpowiedzialnością lub nie posiadała wszystkich akcji spółki akcyjnej to takie informacje nie są publikowane i nie są widoczne w Dziale 1 KRS. Dlatego nawet jeżeli powyżej nie ukazuje się żadna spółka z ograniczoną odpowiedzialnością lub spółka akcyjna to nie oznacza to, że osoba zmarła nie była udziałowcem lub akcjonariuszem żadnego przedsiębiorstwa.',
          italics: true,
          fontSize: 10,
          lineHeight: 1.3,
        },
        {
          text: 'Jak interpretować powyższe informacje?',
          bold: true,
          fontSize: 10,
          lineHeight: 1.3,
          marginTop: 20,
        },
        {
          text: 'Ten raport zawiera informacje, czy osoba zmarła kiedykolwiek występowała w Centralnej Ewidencji i Informacji o Działalności Gospodarczej lub w Krajowym Rejestrze Sądowym. Nie zawsze jest to informacja, czy osoba zmarła znajdowała się w tych rejestrach w dniu jej śmierci. Informacja, że osoba zmarła prowadziła działalność gospodarczą może oznaczać, że prowadziła ją np. 10 lat temu lub 3 miesiące temu, ale nie została jeszcze wykreślona z CEIDG. Tak samo należy interpretować informację z Krajowego Rejestru Sądowego. Informacja, że osoba zmarła posiadała udziały lub akcje w spółkach oznacza, że mogła posiadać je 10 lat temu lub 3 miesiące temu, ale tych udziałów być może już nie posiada. Niemniej jest to bardzo wartościowa informacja dla potencjalnego spadkobiercy, ponieważ może oznaczać posiadanie przez osobę zmarłą aktywów lub długów.Jeżeli chcesz uzyskać bardziej szczegółowe informacje o sytuacji osoby zmarłej w tych rejestrach np. o szczegółach prowadzonej jednoosobowej działalność gospodarczej, czy na temat tego w jakich konkretnie spółkach posiadała udziały lub w jakiej ilości możesz je otrzymać w Raporcie Rozszerzonym.',
          italics: true,
          fontSize: 10,
          lineHeight: 1.3,
        },
      ],

      styles: {
        header: {
          fontSize: 18,
          bold: true,
          margin: [0, 0, 0, 20],
        },
        table: {
          fontSize: 16,
          margin: [0, 5, 0, 5],
        },
      },
      header: () => {
        return {
          table: {
            body: [
              [
                {
                  svg: '<svg version="1.0" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0" y="0" width="2400" height="562.1704344824068" viewBox="80.6834327697754 104.07868881225586 178.63314971923828 41.84261474609375" preserveAspectRatio="xMidYMid meet" color-interpolation-filters="sRGB"><g><defs><linearGradient id="92" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#fa71cd"></stop> <stop offset="100%" stop-color="#9b59b6"></stop></linearGradient><linearGradient id="93" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#f9d423"></stop> <stop offset="100%" stop-color="#f83600"></stop></linearGradient><linearGradient id="94" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#0064d2"></stop> <stop offset="100%" stop-color="#1cb0f6"></stop></linearGradient><linearGradient id="95" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#f00978"></stop> <stop offset="100%" stop-color="#3f51b1"></stop></linearGradient><linearGradient id="96" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#7873f5"></stop> <stop offset="100%" stop-color="#ec77ab"></stop></linearGradient><linearGradient id="97" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#f9d423"></stop> <stop offset="100%" stop-color="#e14fad"></stop></linearGradient><linearGradient id="98" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#009efd"></stop> <stop offset="100%" stop-color="#2af598"></stop></linearGradient><linearGradient id="99" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#ffcc00"></stop> <stop offset="100%" stop-color="#00b140"></stop></linearGradient><linearGradient id="100" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#d51007"></stop> <stop offset="100%" stop-color="#ff8177"></stop></linearGradient><linearGradient id="102" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#a2b6df"></stop> <stop offset="100%" stop-color="#0c3483"></stop></linearGradient><linearGradient id="103" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#7ac5d8"></stop> <stop offset="100%" stop-color="#eea2a2"></stop></linearGradient><linearGradient id="104" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#00ecbc"></stop> <stop offset="100%" stop-color="#007adf"></stop></linearGradient><linearGradient id="105" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stop-color="#b88746"></stop> <stop offset="100%" stop-color="#fdf5a6"></stop></linearGradient></defs><g fill="#4a93fa" class="iconlinesvg-g iconlinesvg" transform="translate(82.43473827838898,105.82850074768066)"><g class="tp-name"><g transform="translate(0, 9.766119447036946)"><g><g class="imagesvg"><g><rect fill="#4a93fa" fill-opacity="0" stroke-width="2" x="0" y="0" width="32.32437316467133" height="18.809753425781544" class="image-rect"></rect> <svg x="0" y="0" width="32.32437316467133" height="18.809753425781544" filtersec="colorsb3681624400" class="image-svg-svg primary" style="overflow: visible;"><svg xmlns="http://www.w3.org/2000/svg" viewBox="-0.0008234977722167969 -0.0005435943603515625 166.71340942382812 97.01138305664062"><path d="M104.65 75.14c-.47 9.73-6.41 17.94-15.44 20.76s-19-.13-24-8.33Q48.47 59.55 32.54 31C26.93 20.92 31 8.48 40.88 2.94 51.15-2.84 63.6.06 69.64 10.24 80.49 28.53 91.06 47 101.53 65.49c1.63 2.89 2.11 6.42 3.12 9.65zM115.18 0a20.32 20.32 0 0 1 17.13 10.4c10.63 18.21 21.26 36.43 31.62 54.8a21.12 21.12 0 0 1-7.69 28.71c-10 5.93-22.81 3.21-28.87-6.92-11-18.38-21.83-36.9-32.13-55.69C87.48 17.14 98 .1 115.18 0z" fill="#4a93fa"></path><path d="M0 75.33C-.06 63 9.13 53.62 21.25 53.68a21.34 21.34 0 0 1 .06 42.68A20.85 20.85 0 0 1 0 75.33z" fill="#1378c0"></path></svg></svg> </g></g></g></g><g transform="translate(36, 0)"><g fill="#4a93fa" transform="scale(1)"><path d="M20.49-23.46L21.87-25.74C19.59-27.72 16.75-29.07 12.65-29.07C6.89-29.07 2.36-26.52 2.36-20.78C2.36-13.97 10.02-13.88 15.37-12.28C17.91-11.52 20.29-10.43 20.29-7.28C20.29-3.52 17.00-2.28 12.97-2.28C8.45-2.28 4.94-3.93 2.72-6.26L1.30-3.94C4.55-1.15 8.42 0.24 12.89 0.24C18.62 0.24 23.30-1.86 23.30-7.56C23.30-14.76 15.37-14.88 9.70-16.55C7.29-17.25 5.29-18.21 5.29-21.18C5.29-25.09 8.27-26.55 12.60-26.55C16.06-26.55 18.88-25.29 20.49-23.46Z" transform="translate(-1.3011068443641292, 29.681499887056702)"></path></g></g><g transform="translate(61, 0)"><g fill="#4a93fa" transform="scale(1)"><path d="M5.53-17.08L5.53-21.18L3.05-21.18L3.05 8.66L5.81 8.66L5.81-4.39C7.33-1.90 10.02 0.41 13.86 0.41C15.27 0.41 16.57 0.12 17.75-0.47C21.09-2.12 23.46-5.66 23.46-10.57C23.46-13.73 22.19-16.41 20.61-18.20C19.02-20.01 16.64-21.55 13.42-21.55C9.80-21.55 7.05-19.40 5.53-17.08ZM13.09-2.03C12.31-2.03 11.55-2.17 10.82-2.44C8.49-3.30 6.78-5.11 5.81-7.36L5.81-13.86C5.92-14.57 6.21-15.24 6.69-15.88C7.87-17.47 10.11-19.11 12.77-19.11C13.88-19.11 14.92-18.88 15.88-18.42C18.51-17.16 20.66-14.39 20.66-10.57C20.66-9.49 20.48-8.44 20.13-7.42C19.15-4.61 16.82-2.03 13.09-2.03Z" transform="translate(-3.0494691664784277, 29.681499887056702)"></path></g></g><g transform="translate(85, 0)"><g fill="#4a93fa" transform="scale(1)"><path d="M16.83-3.54L16.91-1.87C16.97-0.59 17.83 0.12 19.19 0.12C19.44 0.12 19.79 0.08 20.25 0L20.25-2.44C19.57-2.44 19.23-2.82 19.23-3.58L19.23-13.13C19.23-18.40 15.97-21.55 10.69-21.55C8.12-21.55 5.46-20.63 2.72-18.78L3.70-16.96C5.57-18.26 7.75-19.48 10.49-19.48C14.22-19.48 16.47-17.10 16.47-13.38L16.47-11.47C14.84-12.13 12.44-12.56 10.21-12.56C8.90-12.56 7.71-12.41 6.63-12.10C3.94-11.32 1.34-9.52 1.34-6.18C1.34-5.26 1.52-4.40 1.87-3.60C2.87-1.33 5.14 0.41 8.38 0.41C12.17 0.41 15.09-1.26 16.83-3.54ZM10.57-10.82C12.81-10.82 14.77-10.37 16.47-9.72L16.47-6.26C16.47-5.61 16.11-5.05 15.74-4.68C14.53-2.95 11.94-1.71 9.03-1.71C6.21-1.71 3.98-3.56 3.98-6.38C3.98-9.43 7.27-10.82 10.57-10.82Z" transform="translate(-1.3417664332505082, 29.681499887056702)"></path></g></g><g transform="translate(107, 0)"><g fill="#4a93fa" transform="scale(1)"><path d="M19.64-4.11L19.64-2.03C19.64-0.81 20.84 0.12 22.08 0.12C22.32 0.12 22.65 0.08 23.05 0L23.05-2.44C22.40-2.44 22.08-2.82 22.08-3.58L22.08-29.68L19.31-29.68L19.31-16.75C17.87-19.19 15.08-21.55 11.30-21.55C9.89-21.55 8.60-21.26 7.42-20.68C4.10-19.03 1.63-15.42 1.63-10.53C1.63-9.09 1.88-7.72 2.38-6.40C3.75-2.82 6.89 0.41 11.75 0.41C15.29 0.41 18.20-1.74 19.64-4.11ZM12.36-2.03C11.25-2.03 10.21-2.26 9.25-2.72C6.61-3.99 4.47-6.70 4.47-10.53C4.47-11.62 4.65-12.67 5.00-13.70C5.96-16.52 8.28-19.11 11.99-19.11C12.73-19.11 13.47-18.97 14.23-18.70C16.51-17.89 18.48-16.08 19.31-13.82L19.31-7.28C19.20-6.55 18.91-5.86 18.44-5.22C17.26-3.64 15.00-2.03 12.36-2.03Z" transform="translate(-1.6263835554551616, 29.681499887056702)"></path></g></g><g transform="translate(132, 0)"><g fill="#4a93fa" transform="scale(1)"><path d="M12.32-1.71C11.29-1.71 10.32-1.90 9.41-2.30C6.74-3.45 4.73-6.04 4.47-9.60L22.61-9.60C22.63-9.68 22.65-9.83 22.65-10.04L22.65-10.69C22.65-12.16 22.39-13.54 21.87-14.84C20.42-18.51 17.20-21.55 12.16-21.55C10.64-21.55 9.23-21.26 7.93-20.68C4.40-19.09 1.59-15.66 1.59-10.65C1.59-9.13 1.85-7.71 2.38-6.38C3.85-2.69 7.12 0.41 12.20 0.41C16.56 0.41 19.93-1.76 21.22-4.88L18.83-5.53C17.89-3.35 15.38-1.71 12.32-1.71ZM12.20-19.39C13.23-19.39 14.20-19.20 15.11-18.83C17.77-17.71 19.75-15.21 20.00-11.71L4.43-11.71C4.51-12.82 4.76-13.85 5.18-14.80C6.30-17.32 8.65-19.39 12.20-19.39Z" transform="translate(-1.5857239665687823, 29.681499887056702)"></path></g></g><g transform="translate(157, 0)"><g fill="#4a93fa" transform="scale(1)"><path d="M5.81 0L5.81-6.63L10.37-10.86L18.17 0L21.18 0L12.20-12.48L20.86-21.14L17.73-21.14L5.81-9.51L5.81-29.68L3.05-29.68L3.05 0Z" transform="translate(-3.049469166478428, 29.681499887056702)"></path></g></g></g> </g><defs v-gra="od"></defs></g></svg>',
                  width: 125,
                },
                {
                  text: [
                    { text: 'Numer raportu: ', bold: true, alignment: 'right' },
                    {
                      text: `${report.reportNumber?.toUpperCase()}`,
                      alignment: 'right',
                    },
                  ],
                  fontSize: 10,
                },
              ],
            ],
            widths: ['auto', '*'],
          },

          margin: [40, 40, 40, 0],
          layout: 'noBorders',
        };
      },
      footer: (currentPage: number, pageCount: number) => {
        return {
          table: {
            body: [
              [
                {
                  text: 'mspadek.pl',
                  link: 'https://mspadek.pl',
                },
                {
                  text: `${currentPage}/${pageCount}`,
                  alignment: 'right',
                },
              ],
            ],
            widths: ['80%', '20%'],
          },
          margin: [40, 0, 40, 40],
          layout: 'noBorders',
          fontSize: 10,
        };
      },
      pageMargins: [40, 100, 40, 40],
      defaultStyle: {
        font: 'HelveticaNeue',
      },
    };

    const sanitizedReportNumber = report.reportNumber.replaceAll('/', '_');

    const fileName= `Raport_${sanitizedReportNumber}.pdf`
    pdfMake
      .createPdf(docDef, undefined, fonts)
      .download(fileName)
  }
}
